import React, { useState, useEffect, useContext } from 'react'

import queryString from 'query-string'
import { RouteComponentProps } from 'react-router'

import { Grid, Header, Confirm, Button, Image, Label, Divider } from 'semantic-ui-react'
import { ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import GridOnIcon from '@mui/icons-material/GridOn'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'

import ImageVloggiOctopus from '../../_images/vloggi-octopus.jpeg'

import Data, { IProject } from '../../api/Data'
import { GlobalContext } from '../../contexts/GlobalContext'
import { UserContext } from '../../contexts/UserContext'
import OnboardingPage from '../onboarding/OnboardingPage'

import Section from '../../components/common/Section'
import SubSection from '../../components/common/SubSection'
import Title from '../../components/common/Title'

import ProjectCard from './ProjectCard'
import ProjectStartProjectModal from './ProjectStartProjectModal'

import moment from '../../utils/Moment'
import { formatNumber } from '../../utils/sharedFunctions'

const ProjectIndexPage = ({ history, location }: IProps) => {
  const { dispatchGlobalContext } = useContext(GlobalContext)
  const { userProfile } = useContext(UserContext)

  // States
  const [projects, setProjects] = useState<IProject[] | null>(null)
  const [showOnboardingModal, setShowOnboardingModal] = useState<boolean>(false)
  const [showStartProjectModal, setShowStartProjectModal] = useState<boolean>(false)
  const [showDeletionConfModal, setShowDeletionConfModal] = useState<string | null>(null)
  const [isTableView, setIsTableView] = useState(false)

  // Show welcome modal if new user
  useEffect(() => {
    const { newUser } = queryString.parse(location.search)
    if (newUser) setShowOnboardingModal(true)
  }, [location.search])

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      const { data, error } = await Data.getProjects()

      if (error) return

      setProjects(data)
    }

    fetchProjects()
  }, [location.search])


  const handleDuplicateProject = async (slug: string) => {
    const { data, error } = await Data.duplicateProject(slug)

    if (error) {
      if (error.response.status === 403) {
        return dispatchGlobalContext({ type: 'show update plan modal' })
      }
      else {
        return alert(error)
      }
    }

    // Add project to project gallery
    const newProject = data
    setProjects((prevState) => [newProject, ...prevState!])
  }

  const deleteProject = async (slug: string) => {
    const { error } = await Data.deleteProject(slug)

    if (error) {
      setShowDeletionConfModal(null)
      return alert('An error occurred')
    }

    // Remove deleted project from the array
    setProjects((prevState) => [...prevState!.filter((el) => el.slug !== slug)])

    setShowDeletionConfModal(null)
  }

  const goToProjectShowPage = (slug: string) => {
    history.push(`/projects/${slug}/configure?tab=overview`)
  }

  const goToTemplatePage = () => {
    history.push(`/themes`)
  }

  // Is user collaborator
  const isUserCollaborator = (): boolean => {
    return userProfile.userAccessRights === 'collaborator'
  }
  
  if (!projects) return null

  console.log('Rendering projects page', projects)

  return (
    <Section>
      <>
        <Title title="">
          <>
            {projects.length !== 0 && (
              <ToggleButtonGroup
                size="small"
                value={isTableView ? 'list' : 'grid'}
                exclusive
                aria-label="view mode"
                onChange={(event, newViewMode) => {
                  if (newViewMode !== null) {
                    setIsTableView(newViewMode === 'list')
                  }
                }}
                sx={{
                  marginRight: '1rem',
                  color: 'white'
                }}
              >
                <ToggleButton 
                  value="grid" 
                  aria-label="grid view" 
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    backgroundColor: !isTableView ? 'grey' : 'transparent',
                    gap: '5px',
                    '&.Mui-selected': {
                      color: 'white',
                      borderColor: 'white',
                      backgroundColor: 'grey'
                    },
                    '&:hover': {
                      backgroundColor: !isTableView ? 'grey' : 'transparent'
                    }
                  }}
                >
                  <GridOnIcon/>
                  Grid
                </ToggleButton>
                <ToggleButton 
                  value="list" 
                  aria-label="list view" 
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    backgroundColor: isTableView ? 'grey' : 'transparent',
                    gap: '5px',
                    '&.Mui-selected': {
                      color: 'white',
                      borderColor: 'white',
                      backgroundColor: 'grey'
                    },
                    '&:hover': {
                      backgroundColor: isTableView ? 'grey' : 'transparent'
                    }
                  }}
                >
                  <FormatListBulletedIcon />
                  List
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            {projects.length !== 0 && !isUserCollaborator() && (
              <Label color={projects[0].totalNumberOfTeamClips >= projects[0].clipMax ? 'red' : 'grey'} style={{ marginRight: '1rem', padding: '0.75rem' }}>
                Video clips received: {formatNumber(projects[0].totalNumberOfTeamClips)} / {formatNumber(projects[0].clipMax)}
              </Label>
            )}
            {projects.length !== 0 && !isUserCollaborator() && (
              <Button primary compact onClick={() => setShowStartProjectModal(true)}>
                Create a New Project
              </Button>
            )}
          </>
        </Title>

        <SubSection>
          <>
            <Header as="h1" style={{ fontWeight: 'bold' }}>
              Projects
              <Header.Subheader>
                Your curated libraries of crowdsourced video content.
              </Header.Subheader>
            </Header>

            <Divider style={{ margin: '2rem 0' }} />

            {projects.length > 0 ? (
              isTableView ? (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#13165a' }}>
                      <TableCell />
                      <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                        Project&nbsp;Name
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                        Status
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                        Clips
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                        Deadline
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                        Page&nbsp;Views
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                        Compilations
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.map((project, i) => (
                      <TableRow
                        key={i}
                        onClick={() => goToProjectShowPage(project.slug)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'background-color 0.3s',
                          '&:hover': {
                            backgroundColor: '#f0f0f0'
                          },
                          opacity: project.shared ? 1 : 0.5
                        }}
                      >
                        <TableCell>
                          <img src={project.imageUrl} alt={project.name} style={{ height: '50px', aspectRatio: '16/9', objectFit: 'cover', borderRadius: '4px' }} />
                        </TableCell>
                        <TableCell>
                          {project.name || <em>no name specified</em>}
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontWeight={project.shared ? 'fontWeightBold' : 'normal'}>
                            {project.shared ? 'Active' : 'Inactive'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(project.clipCount || 0)}
                        </TableCell>
                        <TableCell>
                          {project.deadline ? moment(project.deadline).format('ll') : <em>no deadline given</em>}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(project.pageViews || 0)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(project.episodeCount || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Grid columns={4} doubling stackable>
                  {projects.map((project, i) => (
                    <Grid.Column key={i}>
                      <ProjectCard
                        project={project}
                        active={project.shared}
                        goToProjectShowPage={goToProjectShowPage}
                        handleDuplicateProject={handleDuplicateProject}
                        setShowDeletionConfModal={setShowDeletionConfModal}
                      />
                    </Grid.Column>
                  ))}
                </Grid>
              )
            ) : (
              <>
                <Header as="h1" textAlign="center">
                  <Header.Subheader>
                    Plan your projects, manage your contributions and create video stories all from one place. Browse each video project to see the most recent video clips contributed by your community, colleagues or customers.
                  </Header.Subheader>
                </Header>
                <Image src={ImageVloggiOctopus} style={{ margin: '0 auto' }} />
                <Button
                  color="orange"
                  content="Create my first project"
                  onClick={goToTemplatePage}
                  style={{ display: 'block', margin: '2rem auto' }}
                />
              </>
            )}
          </>
        </SubSection>
        <Confirm
          open={!!showDeletionConfModal}
          header="Project deletion"
          content="Are you sure you want to delete this project? All clips will be lost."
          confirmButton="Yes"
          onCancel={() => setShowDeletionConfModal(null)}
          onConfirm={() => deleteProject(showDeletionConfModal!)}
        />
        {showStartProjectModal && (
          <ProjectStartProjectModal setShowModal={setShowStartProjectModal} goToTemplatePage={goToTemplatePage} />
        )}
        {showOnboardingModal && (
          <OnboardingPage />
        )}
      </>
    </Section>
  )
}

interface IProps extends RouteComponentProps<any> {}

export default ProjectIndexPage